import { useEffect, useMemo, type ReactNode } from 'react';

import { forgetHiddenColumns, HiddenColumnsContext } from '../components';
import { useSession } from '../session';
import { useRememberTableColumns } from './queries';

/**
 * Hands every table with an `id` the signed-in person's hidden columns (#444):
 * where they start -- the principal's `hidden_columns` -- and where a change
 * goes -- `PUT /auth/me/table-columns/{table_id}`. The tables themselves, and
 * the in-memory copy they share, are in `components/table/hiddenColumns.ts`.
 *
 * Signed out there is nothing to supply, and what was held is dropped so the
 * next person to sign in on this machine starts from their own principal.
 */
export function HiddenColumnsProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const { mutate } = useRememberTableColumns();
  const principal = session?.principal;

  useEffect(() => {
    if (session === null) forgetHiddenColumns();
  }, [session]);

  const source = useMemo(
    () =>
      principal === undefined
        ? null
        : {
            owner: principal.id,
            stored: principal.hidden_columns,
            remember: (tableId: string, hidden: readonly string[]) => {
              mutate({ tableId, hidden });
            },
          },
    [principal, mutate],
  );

  return <HiddenColumnsContext value={source}>{children}</HiddenColumnsContext>;
}
